import { Loader2, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { FormSectionCard } from '@/components/molecules';
import { PricebookResultRow } from '@/components/molecules/PricebookResultRow';
import {
  formatCurrency,
  type PricebookLineItem,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import { unitLabel } from '@/lib/sales/quantity-display';
import type { TaxSnapshot } from '@/types/tax';
import {
  reconciledTaxBreakdown,
  taxBreakdownDisplayMeta,
  taxBreakdownDisplayName,
  type AggregatedTaxBreakdownRow,
} from '@/lib/tax/tax-utils';

interface Preview {
  lineSubtotal: number;
  lineTax: number;
  lineTotal: number;
  taxSnapshot: TaxSnapshot | null;
}

interface LineItem {
  name: string;
  code: string;
  basePrice: number;
  quantity: number;
  unit: string;
  taxable: boolean;
}

interface EstimateLiteLineItemsSectionProps {
  lineItems: LineItem[];
  lineItemPreviews: Preview[];
  searchQuery: string;
  searchResults: PricebookLineItem[];
  searchLoading: boolean;
  showSearchResults: boolean;
  searchRef: React.RefObject<HTMLDivElement | null>;
  subtotal: number;
  taxTotal: number;
  taxBreakdown: AggregatedTaxBreakdownRow[];
  grandTotal: number;
  taxResolved: boolean;
  zipValue: string;
  onSearchQueryChange: (value: string) => void;
  onSearchFocus: () => void;
  onAddItem: (item: PricebookLineItem) => void;
  onRemoveItem: (index: number) => void;
  onUpdateQuantity: (index: number, quantity: number) => void;
}

export function EstimateLiteLineItemsSection({
  lineItems,
  lineItemPreviews,
  searchQuery,
  searchResults,
  searchLoading,
  showSearchResults,
  searchRef,
  subtotal,
  taxTotal,
  taxBreakdown,
  grandTotal,
  taxResolved,
  zipValue,
  onSearchQueryChange,
  onSearchFocus,
  onAddItem,
  onRemoveItem,
  onUpdateQuantity,
}: EstimateLiteLineItemsSectionProps) {
  return (
    <FormSectionCard className="space-y-3">
      <p className="font-semibold text-sm">
        Line Items <span className="text-destructive">*</span>
      </p>

      <div ref={searchRef} className="relative">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search pricebook..."
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            onFocus={onSearchFocus}
            className="pl-8"
          />
          {searchLoading && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </div>

        {showSearchResults && (
          <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
            {searchResults.map((item) => (
              <div
                key={item.id}
                onMouseDown={(event) => {
                  event.preventDefault();
                  onAddItem(item);
                }}
              >
                <PricebookResultRow
                  name={item.name}
                  code={item.code}
                  type={item.type}
                  basePrice={item.basePrice}
                  unit={item.unit}
                  onAdd={() => onAddItem(item)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {lineItems.length > 0 && (
        <div className="space-y-2">
          {lineItems.map((item, index) => {
            const preview = lineItemPreviews[index];
            const lineTaxBreakdown = reconciledTaxBreakdown([preview], preview.lineTax);
            const unit = unitLabel(item.unit, 1, 'word');
            return (
              <div
                key={`${item.code}-${index}`}
                className="overflow-hidden rounded-lg border bg-muted/40"
              >
                <div className="flex items-center gap-2 p-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <p className="truncate text-sm font-medium">{item.name}</p>
                      {item.taxable && (
                        <span className="shrink-0 rounded-full bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning">
                          Taxable
                        </span>
                      )}
                    </div>
                    {/* A price and what it buys one of — "$40.00 · EA" said the
                        second half in a code. `'word'` already answers "each"
                        for the generic unit; an `|| 'each'` on top of it also
                        answers for a BLANK one, inventing a unit the catalog
                        row does not have. No unit, no separator. */}
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(item.basePrice)}
                      {unit && ` · ${unit}`}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      className="flex h-6 w-6 items-center justify-center rounded border text-muted-foreground hover:bg-muted"
                      onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                    >
                      -
                    </button>
                    <span className="w-6 text-center text-sm">{item.quantity}</span>
                    <button
                      className="flex h-6 w-6 items-center justify-center rounded border text-muted-foreground hover:bg-muted"
                      onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>

                  <div className="w-20 shrink-0 text-right">
                    <p className="text-sm font-medium">{formatCurrency(preview.lineSubtotal)}</p>
                    {preview.lineTax > 0 && (
                      <div className="text-xs text-muted-foreground">
                        <p>+{formatCurrency(preview.lineTax)} tax</p>
                        {lineTaxBreakdown.map((row, taxIndex) => (
                          <div
                            key={`${row.primary}-${row.secondary ?? ''}-${row.jurisdictionType ?? ''}-${row.rate}-${taxIndex}`}
                            className="mt-1 text-[10px] leading-tight"
                          >
                            <p>{taxBreakdownDisplayName(row)}</p>
                            <p className="text-muted-foreground/70">
                              {taxBreakdownDisplayMeta(row)} · {formatCurrency(row.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button variant="ghost" size="icon-sm" onClick={() => onRemoveItem(index)}>
                    <Trash2 className="size-3.5 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            );
          })}

          <div className="mt-1 space-y-1.5 rounded-lg border bg-card p-3 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {taxTotal > 0 &&
              (taxBreakdown.length > 0 ? (
                taxBreakdown.map((row, index) => (
                  <div
                    key={`${row.primary}-${row.secondary ?? ''}-${row.jurisdictionType ?? ''}-${row.rate}-${index}`}
                    className="flex items-start justify-between gap-3 text-muted-foreground"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span>{taxBreakdownDisplayName(row)}</span>
                      <span className="text-xs text-muted-foreground/70">
                        {taxBreakdownDisplayMeta(row)}
                      </span>
                    </span>
                    <span>{formatCurrency(row.amount)}</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between text-muted-foreground">
                  <span>Tax</span>
                  <span>{formatCurrency(taxTotal)}</span>
                </div>
              ))}
            {taxTotal === 0 && !taxResolved && zipValue.length < 5 && (
              <p className="text-xs italic text-muted-foreground">Enter ZIP to calculate tax</p>
            )}
            <div className="flex justify-between border-t pt-1.5 font-semibold">
              <span>Total</span>
              <span>{formatCurrency(grandTotal)}</span>
            </div>
          </div>
        </div>
      )}
    </FormSectionCard>
  );
}
